import {
  useId,
  useState,
  type ChangeEvent,
  type ComponentProps,
  type CSSProperties,
  type FocusEvent,
  type ReactNode,
  type Ref,
} from 'react';
import { IconButton } from './IconButton';
import styles from './TextField.module.css';

export interface TextFieldProps extends Omit<ComponentProps<'input'>, 'size' | 'prefix' | 'ref'> {
  /** Floating label (Material labelText). */
  label?: string;
  /** Placeholder (hintText). */
  hint?: string;
  /** Error message; turns the outline and label red. */
  error?: string | null;
  /**
   * Error styling (red outline and label, `aria-invalid`) without a message of
   * its own, e.g. when the reason is shown elsewhere. `error` implies it.
   */
  invalid?: boolean;
  helperText?: string;
  /** Trailing text such as "min" (suffixText). */
  suffixText?: string;
  /** Leading element inside the field. */
  prefix?: ReactNode;
  /** Trailing element (e.g. an IconButton). */
  suffix?: ReactNode;
  /** Obscured text with an eye toggle. `fill` uses eye_fill/eye_slash_fill, `outline` eye/eye_slash. */
  password?: boolean | 'fill' | 'outline';
  /** isDense: ~40 px tall instead of ~56. */
  dense?: boolean;
  /** Textarea that grows with its text between `minRows` and `maxRows` visible rows. */
  multiline?: boolean;
  /** Visible rows of an empty multiline field (default 1). */
  minRows?: number;
  /** Rows before a multiline field scrolls (default: grows without limit). */
  maxRows?: number;
  /** `outline` = app InputDecorationTheme (filled + 1 px outline, 2 px focus); `plain` = filled, no border, 1 px focus. */
  variant?: 'outline' | 'plain';
  onValueChange?: (value: string) => void;
  inputRef?: Ref<HTMLInputElement | HTMLTextAreaElement>;
  inputClassName?: string;
  inputStyle?: CSSProperties;
  /** Class on the decorated box (fill and outline). Theme it with the `--text-field-*` custom properties. */
  boxClassName?: string;
}

/** Min / max heights of a growing textarea (`rows` alone does nothing under `field-sizing: content`). */
export function multilineHeights(minRows: number, maxRows?: number): CSSProperties {
  const min = Math.max(1, Math.floor(minRows));
  const max = maxRows != null ? Math.max(min, Math.floor(maxRows)) : undefined;
  return {
    minHeight: `calc(${min} * 1.35em)`,
    maxHeight: max != null ? `calc(${max} * 1.35em + 2px)` : undefined,
  };
}

/** Material 3 TextField themed by main.dart InputDecorationTheme (spec 01 §4.5). */
export function TextField({
  label,
  hint,
  error,
  invalid = false,
  helperText,
  suffixText,
  prefix,
  suffix,
  password = false,
  dense = false,
  multiline = false,
  minRows = 1,
  maxRows,
  variant = 'outline',
  onValueChange,
  inputRef,
  className,
  style,
  inputClassName,
  inputStyle,
  boxClassName,
  id,
  value,
  defaultValue,
  onChange,
  onFocus,
  onBlur,
  disabled,
  type,
  ...rest
}: TextFieldProps) {
  const autoId = useId();
  const fieldId = id ?? autoId;
  const messageId = `${fieldId}-message`;
  const [focused, setFocused] = useState(false);
  const [uncontrolledValue, setUncontrolledValue] = useState(String(defaultValue ?? ''));
  const [revealed, setRevealed] = useState(false);
  const currentValue = value !== undefined ? String(value) : uncontrolledValue;
  const floating = focused || currentValue.length > 0;
  const hasError = Boolean(error) || invalid;
  const message = error || helperText;

  const handleChange = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    if (value === undefined) setUncontrolledValue(event.target.value);
    onChange?.(event as ChangeEvent<HTMLInputElement>);
    onValueChange?.(event.target.value);
  };
  const common = {
    id: fieldId,
    className: [styles.input, inputClassName ?? ''].filter(Boolean).join(' '),
    style: inputStyle,
    value: currentValue,
    disabled,
    placeholder: label && !floating ? undefined : hint,
    'aria-invalid': hasError || undefined,
    'aria-describedby': message ? messageId : undefined,
    onChange: handleChange,
    onFocus: (event: FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setFocused(true);
      onFocus?.(event as FocusEvent<HTMLInputElement>);
    },
    onBlur: (event: FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setFocused(false);
      onBlur?.(event as FocusEvent<HTMLInputElement>);
    },
  };

  const passwordStyle = password === 'outline' ? 'outline' : 'fill';
  const eyeIcon = revealed
    ? passwordStyle === 'fill'
      ? 'eye_slash_fill'
      : 'eye_slash'
    : passwordStyle === 'fill'
      ? 'eye_fill'
      : 'eye';

  return (
    <div className={[styles.field, className ?? ''].filter(Boolean).join(' ')} style={style}>
      <div
        className={[
          styles.box,
          styles[variant],
          dense ? styles.dense : '',
          multiline ? styles.multiline : '',
          focused ? styles.focused : '',
          hasError ? styles.error : '',
          disabled ? styles.disabled : '',
          boxClassName ?? '',
        ]
          .filter(Boolean)
          .join(' ')}
        data-focused={focused || undefined}
        data-invalid={hasError || undefined}
      >
        {prefix && <span className={styles.prefix}>{prefix}</span>}
        <div className={styles.inputWrap}>
          {label && (
            <label htmlFor={fieldId} className={[styles.label, floating ? styles.labelFloating : ''].filter(Boolean).join(' ')}>
              {label}
            </label>
          )}
          {multiline ? (
            <textarea
              {...(rest as ComponentProps<'textarea'>)}
              {...common}
              ref={inputRef as Ref<HTMLTextAreaElement>}
              rows={minRows}
              // field-sizing: content ignores `rows`, so the row limits are heights (line height 1.35).
              style={{ ...multilineHeights(minRows, maxRows), ...inputStyle }}
            />
          ) : (
            <input
              {...rest}
              {...common}
              ref={inputRef as Ref<HTMLInputElement>}
              type={password && !revealed ? 'password' : password ? 'text' : type}
            />
          )}
        </div>
        {suffixText && floating && <span className={styles.suffixText}>{suffixText}</span>}
        {password && (
          <IconButton
            icon={eyeIcon}
            label={revealed ? 'Hide password' : 'Show password'}
            className={styles.suffixButton}
            onClick={() => setRevealed((r) => !r)}
            disabled={disabled}
            tooltip={passwordStyle === 'outline'}
          />
        )}
        {suffix && <span className={styles.suffix}>{suffix}</span>}
      </div>
      {message && (
        <p id={messageId} className={[styles.message, error ? styles.messageError : ''].filter(Boolean).join(' ')} role={error ? 'alert' : undefined}>
          {message}
        </p>
      )}
    </div>
  );
}
